if(!customElements.get('compare-sizes')) {
  class CompareSizes extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.options = {};
      this.resizeObserver = null;
      this.lastWidth = 0;
      this.rowFrame = 0;
      this.toggleButton = null;
      this.handleToggleBound = this.handleToggle.bind(this);
    }
  
    initSlider() {
      if (!this.querySelector('[data-slider]')) return;
  
      const wrapper = this;
      this.options = {
        slidesPerView: 1.5,
        navigation: {
          prevEl: this.querySelector('.swiper-arrow--prev'),
          nextEl: this.querySelector('.swiper-arrow--next'),
        },
        breakpoints: {
          750: {
            slidesPerView: 2
          },
          1024: {
            slidesPerView: 3
          }
        },
        on: {
          init: function () {
            updateAriaLabels()
          },
          slideChange: function () {
            updateAriaLabels()
          }
        },
      };

      function updateAriaLabels() {
        const __slides = wrapper.querySelectorAll('.compare-size--block');

        setTimeout(function() {
          __slides.forEach((slide) => {
            slide.removeAttribute('role');
            slide.removeAttribute('aria-label');
          })
        }, 500);
      }
  
      this.slider = new Swiper(
        this.querySelector('[data-slider]'),
        this.options,
      );
    }

    /** Watches the column width, so the rows match again when it changes. */
    initRowHeights() {
      const column = this.querySelector('.compare-size--block');
      if (!column) return;

      this.lastWidth = 0;
      this.resizeObserver = new ResizeObserver((entries) => this.handleResize(entries));
      this.resizeObserver.observe(column);
    }

    /**
     * Matches the rows again only when the column width changes.
     * The row match changes the height, so height-only changes are ignored.
     * @param {ResizeObserverEntry[]} entries
     */
    handleResize(entries) {
      const width = entries[0].contentRect.width;
      if (width === this.lastWidth) return;

      this.lastWidth = width;
      this.scheduleEqualizeRows();
    }

    /**
     * Runs `equalizeRows` and `updateCollapseHeight` on the next frame, and merges repeat calls into one.
     * A write inside the ResizeObserver callback causes a "ResizeObserver loop" error.
     */
    scheduleEqualizeRows() {
      cancelAnimationFrame(this.rowFrame);
      this.rowFrame = requestAnimationFrame(() => {
        this.equalizeRows();
        this.updateCollapseHeight();
      });
    }

    /** Sets each `[data-row]` cell to the height of the tallest cell with the same `data-row`. */
    equalizeRows() {
      const rows = new Map();

      for (const cell of this.querySelectorAll('[data-row]')) {
        cell.style.minHeight = '';
        const row = rows.get(cell.dataset.row) ?? [];
        row.push(cell);
        rows.set(cell.dataset.row, row);
      }

      // Read all heights before any write, so the browser runs layout once.
      const heights = new Map();
      for (const [key, row] of rows) {
        heights.set(key, Math.max(...row.map((cell) => cell.getBoundingClientRect().height)));
      }

      for (const [key, row] of rows) {
        for (const cell of row) cell.style.minHeight = `${heights.get(key)}px`;
      }
    }

    /** Stores the bottom of the `data-collapse-row` row, so CSS can set the collapsed height on mobile. */
    updateCollapseHeight() {
      const slider = this.querySelector('[data-slider]');
      const cell = this.querySelector(`[data-row="${this.dataset.collapseRow}"]`);
      if (!slider || !cell) return;

      const bottom = cell.getBoundingClientRect().bottom - slider.getBoundingClientRect().top;
      this.style.setProperty('--cs-collapse-height', `${bottom}px`);
    }

    /** Expands or collapses the columns on mobile. */
    handleToggle() {
      const expanded = this.toggleButton.getAttribute('aria-expanded') !== 'true';
      const { labelMore, labelLess } = this.toggleButton.dataset;

      this.classList.toggle('is-expanded', expanded);
      this.toggleButton.setAttribute('aria-expanded', expanded);
      this.toggleButton.querySelector('[data-toggle-label]').textContent = expanded ? labelLess : labelMore;

      // Collapsing can move the button above the viewport.
      if (!expanded && this.toggleButton.getBoundingClientRect().top < 0) {
        this.toggleButton.scrollIntoView({ block: 'center' });
      }
    }

    connectedCallback() {
      this.initSlider();
      this.initRowHeights();

      this.toggleButton = this.querySelector('[data-toggle]');
      this.toggleButton?.addEventListener('click', this.handleToggleBound);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.rowFrame);
      this.slider?.destroy(true, true);
      this.toggleButton?.removeEventListener('click', this.handleToggleBound);
    }
  }
  
  customElements.define('compare-sizes', CompareSizes);
}